// src/lib/api.ts
import { getToken } from "./auth";

const API_URL = import.meta.env.VITE_API_URL;

function isPlainObject(v: any) {
  return v != null && typeof v === "object" && (v.constructor === Object || Object.getPrototypeOf(v) === Object.prototype);
}

export async function apiFetch<T = any>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const token = getToken();

  const headers = new Headers(options.headers || {});
  headers.set("Accept", "application/json");

  let body: any = (options as any).body;

  // ✅ If body is a plain object, JSON stringify it
  if (isPlainObject(body)) {
    headers.set("Content-Type", "application/json");
    body = JSON.stringify(body);
  }

  // ✅ If body is already string, keep it JSON if caller didn't set
  if (typeof body === "string" && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  // ✅ If body is FormData, do NOT set Content-Type (browser sets boundary)
  if (typeof FormData !== "undefined" && body instanceof FormData) {
    headers.delete("Content-Type");
  }

  if (token) headers.set("Authorization", `Bearer ${token}`);

  const res = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers,
    body,
  });

  const data = await res.json().catch(() => null);

  if (!res.ok) {
    const message =
      data?.errors?.email?.[0] ||
      data?.message ||
      `Request failed (${res.status})`;
    throw new Error(message);
  }

  return data as T;
}

// optional wrappers (still fine)
export const api = {
  get: <T = any>(endpoint: string) => apiFetch<T>(endpoint),
  post: <T = any>(endpoint: string, body?: any) =>
    apiFetch<T>(endpoint, { method: "POST", body: body == null ? undefined : body }),
  put: <T = any>(endpoint: string, body?: any) =>
    apiFetch<T>(endpoint, { method: "PUT", body: body == null ? undefined : body }),
  del: <T = any>(endpoint: string) => apiFetch<T>(endpoint, { method: "DELETE" }),
};